import type {
  ItemRelationshipEdge,
  ItemRelationshipGroup,
  ItemRelationshipGroupMembership,
} from '@/types/prioritization/itemRelationships';

export type RelationshipReviewItem = {
  id: string;
  title?: string;
};

export type TrackRelationshipSummary = {
  itemsNeedingReview: number;
  candidateCount: number;
  hardDependencyOutsideTrackCount: number;
  activeGroupCount: number;
  totalFlags: number;
  needsReview: boolean;
};

export const EMPTY_TRACK_RELATIONSHIP_SUMMARY: TrackRelationshipSummary = {
  itemsNeedingReview: 0,
  candidateCount: 0,
  hardDependencyOutsideTrackCount: 0,
  activeGroupCount: 0,
  totalFlags: 0,
  needsReview: false,
};

export type RelationshipGroupReviewEntry = {
  id: string;
  type: ItemRelationshipGroup['type'];
  subtype?: ItemRelationshipGroup['subtype'];
  status: ItemRelationshipGroup['status'];
  declaredMode: ItemRelationshipGroup['declaredMode'];
  title?: string;
  rationale?: string;
  members: Array<{
    itemId: string;
    title: string;
    inTrack: boolean;
    membershipRole?: ItemRelationshipGroupMembership['membershipRole'];
  }>;
};

export type RelationshipEdgeReviewEntry = {
  id: string;
  type: ItemRelationshipEdge['type'];
  subtype?: ItemRelationshipEdge['subtype'];
  status: ItemRelationshipEdge['status'];
  declaredMode: ItemRelationshipEdge['declaredMode'];
  directionLabel: string;
  otherItemId: string;
  otherItemTitle: string;
  otherItemInTrack: boolean;
  label?: string;
  rationale?: string;
};

export type ItemRelationshipReview = {
  summary: TrackRelationshipSummary;
  blockingReasons: string[];
  informationalReasons: string[];
  groups: RelationshipGroupReviewEntry[];
  edges: RelationshipEdgeReviewEntry[];
};

export function summarizeItemRelationshipState(
  itemId: string,
  trackItemIds: Set<string>,
  groups: ItemRelationshipGroup[],
  memberships: ItemRelationshipGroupMembership[],
  edges: ItemRelationshipEdge[],
): TrackRelationshipSummary {
  const membershipsForItem = memberships.filter((membership) => membership.itemId === itemId);
  const groupIds = new Set(membershipsForItem.map((membership) => membership.groupId));
  const groupsForItem = groups.filter((group) => groupIds.has(group.id));
  const edgesForItem = edges.filter((edge) => edge.sourceItemId === itemId || edge.targetItemId === itemId);

  const candidateCount =
    groupsForItem.filter((group) => group.status === 'candidate').length +
    edgesForItem.filter((edge) => edge.status === 'candidate').length;

  const activeGroupCount = groupsForItem.filter((group) => group.status === 'active' || group.status === 'confirmed').length;

  const hardDependencyOutsideTrackCount = edgesForItem.filter((edge) => {
    if (edge.type !== 'dependency' || edge.subtype !== 'hard') return false;
    const otherItemId = edge.sourceItemId === itemId ? edge.targetItemId : edge.sourceItemId;
    return !trackItemIds.has(otherItemId);
  }).length;

  // A package only needs review when at least one member is missing from the track.
  // If all members are already in scope the package is satisfied regardless of status.
  const packagesWithMissingMembersCount = groupsForItem.filter((group) => {
    if (group.type !== 'package') return false;
    const groupMemberships = memberships.filter((m) => m.groupId === group.id);
    return groupMemberships.some((m) => !trackItemIds.has(m.itemId));
  }).length;

  const totalFlags = candidateCount + hardDependencyOutsideTrackCount + activeGroupCount;

  // An unresolved (candidate) CLUSTER is an identity-ambiguity gate: per Canon
  // Doc 13 §3 (Gate Priority Order, Priority 1 "Cluster resolution") and Doc 12
  // Rule 13 ("Cluster Resolution Gates All Other Relationship Semantics … no
  // track-entry decision while a cluster is unresolved"), every member of a
  // candidate cluster must be reviewed before ranking. This is distinct from a
  // candidate *edge* or other candidate group, which is not itself a blocker.
  const unresolvedClusterCandidate = groupsForItem.some(
    (group) => group.type === 'cluster' && group.status === 'candidate',
  );

  // Readiness is blocked by actual violations:
  //   1. A package whose membership is incomplete in this track
  //   2. A hard dependency on an item outside this track
  //   3. Membership in an unresolved (candidate) cluster — identity gate
  const hasViolation =
    packagesWithMissingMembersCount > 0 ||
    hardDependencyOutsideTrackCount > 0 ||
    unresolvedClusterCandidate;

  return {
    itemsNeedingReview: hasViolation ? 1 : 0,
    candidateCount,
    hardDependencyOutsideTrackCount,
    activeGroupCount,
    totalFlags,
    needsReview: hasViolation,
  };
}

export function summarizeTrackRelationshipReadiness(
  items: Array<{ id: string }>,
  groups: ItemRelationshipGroup[],
  memberships: ItemRelationshipGroupMembership[],
  edges: ItemRelationshipEdge[],
): TrackRelationshipSummary {
  const trackItemIds = new Set(items.map((item) => item.id));

  // Count hard-dep edges outside the pool — one row per edge, not per item.
  const hardDepEdgesOutside = new Set<string>();
  for (const edge of edges) {
    if (edge.status === 'dissolved' || edge.status === 'dismissed') continue;
    if (edge.type !== 'dependency' || edge.subtype !== 'hard') continue;
    const srcInPool = trackItemIds.has(edge.sourceItemId);
    const tgtInPool = trackItemIds.has(edge.targetItemId);
    if (!srcInPool && !tgtInPool) continue;
    const otherId = srcInPool ? edge.targetItemId : edge.sourceItemId;
    if (!trackItemIds.has(otherId)) hardDepEdgesOutside.add(edge.id);
  }

  // Count packages with at least one member missing from the pool — one row per package.
  const packagesWithMissingMembers = new Set<string>();
  for (const group of groups) {
    if (group.status === 'dissolved' || group.status === 'dismissed') continue;
    if (group.type !== 'package') continue;
    const groupMemberships = memberships.filter((m) => m.groupId === group.id);
    const hasPoolMember = groupMemberships.some((m) => trackItemIds.has(m.itemId));
    const hasMissingMember = groupMemberships.some((m) => !trackItemIds.has(m.itemId));
    if (hasPoolMember && hasMissingMember) packagesWithMissingMembers.add(group.id);
  }

  const hasViolation = hardDepEdgesOutside.size > 0 || packagesWithMissingMembers.size > 0;

  // Per-item counts are still needed for the detailed readiness card.
  const summaries = items.map((item) => summarizeItemRelationshipState(item.id, trackItemIds, groups, memberships, edges));
  return {
    itemsNeedingReview: summaries.filter((s) => s.needsReview).length,
    candidateCount: summaries.reduce((sum, s) => sum + s.candidateCount, 0),
    hardDependencyOutsideTrackCount: hardDepEdgesOutside.size,
    activeGroupCount: summaries.reduce((sum, s) => sum + s.activeGroupCount, 0),
    totalFlags: summaries.reduce((sum, s) => sum + s.totalFlags, 0),
    needsReview: hasViolation || summaries.some((s) => s.needsReview),
  };
}

export function getTrackRelationshipBlockerMessage(summary: TrackRelationshipSummary): string | null {
  if (!summary.needsReview) return null;

  const parts: string[] = [];
  if (summary.hardDependencyOutsideTrackCount > 0) {
    parts.push(
      `${summary.hardDependencyOutsideTrackCount} hard dependenc${summary.hardDependencyOutsideTrackCount === 1 ? 'y' : 'ies'} on items outside this track`,
    );
  }
  // Note: a candidate *edge* alone is NOT a violation; an unresolved candidate
  // cluster and packages with missing members are (surfaced via per-item
  // needsReview). The lead-in phrase is stable ("Resolve relationship blockers
  // before running ranking") — two suites assert it (trackRelationshipReview,
  // trackConditionsSummary) — with the specifics appended when available.

  return parts.length > 0
    ? `Resolve relationship blockers before running ranking: ${parts.join('; ')}. Open the Relationships tab.`
    : 'Resolve relationship blockers before running ranking. Open the Relationships tab.';
}

export function buildItemRelationshipReview(
  itemId: string,
  trackItemIds: Set<string>,
  itemsById: Map<string, RelationshipReviewItem>,
  groups: ItemRelationshipGroup[],
  memberships: ItemRelationshipGroupMembership[],
  edges: ItemRelationshipEdge[],
): ItemRelationshipReview {
  const summary = summarizeItemRelationshipState(itemId, trackItemIds, groups, memberships, edges);
  const membershipsForItem = memberships.filter((membership) => membership.itemId === itemId);
  const groupIds = new Set(membershipsForItem.map((membership) => membership.groupId));
  const groupsForItem = groups.filter((group) => groupIds.has(group.id));
  const edgesForItem = edges.filter((edge) => edge.sourceItemId === itemId || edge.targetItemId === itemId);

  const groupsForReview: RelationshipGroupReviewEntry[] = groupsForItem.map((group) => ({
    id: group.id,
    type: group.type,
    subtype: group.subtype,
    status: group.status,
    declaredMode: group.declaredMode,
    title: group.title,
    rationale: group.rationale,
    members: memberships
      .filter((membership) => membership.groupId === group.id)
      .map((membership) => ({
        itemId: membership.itemId,
        title: itemsById.get(membership.itemId)?.title ?? membership.itemId,
        inTrack: trackItemIds.has(membership.itemId),
        membershipRole: membership.membershipRole,
      })),
  }));

  const edgesForReview: RelationshipEdgeReviewEntry[] = edgesForItem.map((edge) => {
    const isSource = edge.sourceItemId === itemId;
    const otherItemId = isSource ? edge.targetItemId : edge.sourceItemId;
    return {
      id: edge.id,
      type: edge.type,
      subtype: edge.subtype,
      status: edge.status,
      declaredMode: edge.declaredMode,
      directionLabel: edge.directionality === 'undirected' ? 'Bidirectional' : isSource ? 'Outgoing' : 'Incoming',
      otherItemId,
      otherItemTitle: itemsById.get(otherItemId)?.title ?? otherItemId,
      otherItemInTrack: trackItemIds.has(otherItemId),
      label: edge.label,
      rationale: edge.rationale,
    };
  });

  const blockingReasons: string[] = [];
  const informationalReasons: string[] = [];

  if (summary.candidateCount > 0) {
    blockingReasons.push(`${summary.candidateCount} unreviewed relationship${summary.candidateCount !== 1 ? 's' : ''} still need review.`);
  }
  if (summary.hardDependencyOutsideTrackCount > 0) {
    blockingReasons.push(
      `${summary.hardDependencyOutsideTrackCount} hard dependenc${summary.hardDependencyOutsideTrackCount === 1 ? 'y is' : 'ies are'} outside this track.`,
    );
  }
  if (summary.activeGroupCount > 0) {
    informationalReasons.push(`${summary.activeGroupCount} confirmed or active group relationship${summary.activeGroupCount !== 1 ? 's' : ''} apply to this item.`);
  }
  if (blockingReasons.length === 0 && informationalReasons.length === 0) {
    informationalReasons.push('No canonical relationship issues are currently flagged for this item.');
  }

  return {
    summary,
    blockingReasons,
    informationalReasons,
    groups: groupsForReview,
    edges: edgesForReview,
  };
}
